import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bookmark, Check, ChevronRight, Star } from "lucide-react";
import api from "../lib/api";
import pharmacyPreviewImage from "../assets/icons/pharmacy.png";

function formatAddress(pharmacy) {
  const addressLine = [pharmacy?.address, pharmacy?.city].filter(Boolean).join(", ");
  const distance = pharmacy?.distance ? ` (${pharmacy.distance})` : "";
  return addressLine ? `${addressLine}${distance}` : "Address unavailable";
}

function PharmacyPreviewSkeleton() {
  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: 24,
        border: "1px solid #e8ebf1",
        padding: 12,
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
      }}
    >
      <div
        style={{
          width: "100%",
          height: 218,
          borderRadius: 22,
          background: "#e5e7eb",
        }}
      />
      <div
        style={{
          marginTop: 14,
          width: 110,
          height: 22,
          borderRadius: 999,
          background: "#eef2ff",
        }}
      />
      <div
        style={{
          marginTop: 12,
          width: "65%",
          height: 18,
          borderRadius: 8,
          background: "#e5e7eb",
        }}
      />
      <div
        style={{
          marginTop: 8,
          width: "85%",
          height: 14,
          borderRadius: 8,
          background: "#e5e7eb",
        }}
      />
    </div>
  );
}

export default function HomePharmacyPreview() {
  const navigate = useNavigate();
  const [pharmacy, setPharmacy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const loadPharmacy = async () => {
      try {
        const res = await api.get("/api/pharmacy");
        if (!mounted) return;

        const firstPharmacy =
          Array.isArray(res?.data) && res.data.length > 0 ? res.data[0] : null;
        setPharmacy(firstPharmacy);
      } catch (error) {
        if (!mounted) return;
        console.error("Failed to load pharmacy preview:", error);
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    };

    loadPharmacy();

    return () => {
      mounted = false;
    };
  }, []);

  const addressText = useMemo(() => formatAddress(pharmacy), [pharmacy]);

  return (
    <section style={{ marginTop: 16, marginBottom: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        <h4
          style={{
            margin: 0,
            fontWeight: 700,
            fontSize: 17,
            color: "#1f2937",
            fontFamily: "Inter, sans-serif",
          }}
        >
          Pharmacy
        </h4>

        <button
          onClick={() => navigate("/all-pharmacies")}
          style={{
            border: "none",
            background: "transparent",
            color: "var(--primary)",
            fontWeight: 700,
            fontSize: 14,
            fontFamily: "Inter, sans-serif",
            padding: 0,
            display: "inline-flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          See All <ChevronRight size={16} />
        </button>
      </div>

      {loading ? (
        <PharmacyPreviewSkeleton />
      ) : !pharmacy ? (
        <div
          style={{
            background: "#ffffff",
            borderRadius: 24,
            border: "1px solid #e8ebf1",
            padding: 18,
            boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
            color: "#6b7280",
            fontSize: 14,
            textAlign: "center",
          }}
        >
          No pharmacies found right now.
        </div>
      ) : (
        <div
          onClick={() => navigate(`/pharmacy/${pharmacy._id}`)}
          style={{
            background: "#ffffff",
            borderRadius: 24,
            border: "1px solid #e8ebf1",
            padding: 12,
            boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
            cursor: "pointer",
          }}
        >
          <div style={{ position: "relative", marginBottom: 14 }}>
            <img
              src={pharmacy?.logo || pharmacyPreviewImage}
              alt={pharmacy?.name || "Pharmacy"}
              style={{
                width: "100%",
                height: 218,
                objectFit: "cover",
                borderRadius: 22,
                display: "block",
                background: "#f7efe1",
              }}
            />

            <button
              type="button"
              aria-label="Save pharmacy"
              onClick={(event) => event.stopPropagation()}
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                width: 38,
                height: 38,
                borderRadius: 14,
                border: "none",
                background: "rgba(91, 84, 76, 0.74)",
                color: "#ffffff",
                display: "grid",
                placeItems: "center",
              }}
            >
              <Bookmark size={18} />
            </button>
          </div>

          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              border: "1px solid var(--primary)",
              background: "#f0ecfb",
              color: "var(--primary)",
              borderRadius: 999,
              padding: "5px 10px",
              fontSize: 10,
              fontWeight: 600,
              fontFamily: "Inter, sans-serif",
              lineHeight: 1,
            }}
          >
            <Check size={11} color="var(--primary)" />
            <span>Personal Care</span>
          </div>

          <div
            style={{
              marginTop: 12,
              fontSize: 16,
              lineHeight: 1.3,
              color: "#273244",
              fontWeight: 700,
              fontFamily: "Inter, sans-serif",
            }}
          >
            {pharmacy?.name}
          </div>

          <div
            style={{
              marginTop: 8,
              color: "#6b7280",
              fontSize: 12,
              lineHeight: 1.4,
              fontFamily: "Inter, sans-serif",
            }}
          >
            {addressText}
          </div>

          <div
            style={{
              marginTop: 10,
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              color: "#4b5563",
              fontSize: 12,
              fontFamily: "Inter, sans-serif",
            }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Star size={14} fill="#f7b928" color="#f7b928" />
              <span style={{ fontWeight: 700 }}>{pharmacy?.rating ?? "-"}</span>
              <span>({pharmacy?.reviews ?? 0})</span>
            </span>
            <span style={{ color: "#9ca3af" }}>•</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  background: "var(--primary)",
                  display: "inline-block",
                }}
              />
              <span>{pharmacy?.deliveryAvailable ? "Incl. Free Delivery" : "In-store only"}</span>
            </span>
          </div>
        </div>
      )}
    </section>
  );
}
